// src/components/pages/index.tsx
import React from 'react';
import { Home, Building2, Users, Briefcase, ArrowRight, CheckCircle, Phone, Mail, MapPin } from 'lucide-react';

interface PageProps {
  isDarkMode: boolean;
  setActiveView: (view: string) => void;
  styles: any;
}

export const HomePage: React.FC<PageProps> = ({
  isDarkMode,
  setActiveView,
  styles
}) => {
  const [activeSlide, setActiveSlide] = React.useState(0);

  const slides = [
    { 
      title: "Architectural Excellence", 
      subtitle: "Crafting dream homes with precision",
      image: "https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
    },
    { 
      title: "Innovative Design", 
      subtitle: "Where vision meets reality",
      image: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
    },
    { 
      title: "Sustainable Living", 
      subtitle: "Building for tomorrow, today",
      image: "https://images.pexels.com/photos/2102587/pexels-photo-2102587.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
    }
  ];

  const services = [
    { 
      icon: <Home size={40} />, 
      title: "Custom Homes", 
      desc: "Bespoke residences tailored to your vision",
      image: "https://images.pexels.com/photos/186077/pexels-photo-186077.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
    },
    { 
      icon: <Building2 size={40} />, 
      title: "ADUs", 
      desc: "Elegant accessory dwelling units",
      image: "https://images.pexels.com/photos/2251247/pexels-photo-2251247.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
    },
    { 
      icon: <Users size={40} />, 
      title: "Multi-Unit", 
      desc: "Sophisticated multi-family developments",
      image: "https://images.pexels.com/photos/2462015/pexels-photo-2462015.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
    },
    { 
      icon: <Briefcase size={40} />, 
      title: "Major Remodels", 
      desc: "Transformative luxury renovations",
      image: "https://images.pexels.com/photos/1643389/pexels-photo-1643389.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop"
    }
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <div style={{ paddingTop: '60px', minHeight: '100vh' }}>
      {/* Hero Section with Background Image */}
      <div style={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        padding: '2rem',
        position: 'relative',
        overflow: 'hidden',
        backgroundImage: `url(${slides[activeSlide].image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        transition: 'background-image 1s ease-in-out'
      }}>
        {/* Dark overlay */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1
        }} />
        
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 style={{
            fontSize: 'clamp(3rem, 6vw, 5rem)',
            fontWeight: 'bold',
            marginBottom: '1rem',
            color: '#fff',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            letterSpacing: '0.02em'
          }}>
            {slides[activeSlide].title}
          </h1>
          <p style={{
            fontSize: 'clamp(1.3rem, 2.5vw, 1.8rem)',
            color: '#fff',
            marginBottom: '3rem',
            textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
          }}>
            {slides[activeSlide].subtitle}
          </p>
          <button
            onClick={() => setActiveView('contact')}
            style={{
              padding: '1.2rem 3rem',
              fontSize: '1.2rem',
              backgroundColor: '#fff',
              color: '#000',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s',
              fontWeight: 'bold',
              letterSpacing: '0.05em',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.05)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
            }}
          >
            START YOUR PROJECT
          </button>
        </div>
        
        {/* Slide indicators */}
        <div style={{
          position: 'absolute',
          bottom: '2rem',
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: '0.5rem',
          zIndex: 2
        }}>
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveSlide(index)}
              style={{
                width: index === activeSlide ? '30px' : '10px',
                height: '10px',
                backgroundColor: index === activeSlide ? '#fff' : 'rgba(255,255,255,0.5)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
            />
          ))}
        </div>
      </div>

      {/* Services Section with Images */}
      <div style={{
        padding: '5rem 2rem',
        backgroundColor: isDarkMode ? '#0a0a0a' : '#f8f8f8'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: '3rem',
            textAlign: 'center',
            marginBottom: '1rem',
            fontWeight: 'bold'
          }}>
            Our Services
          </h2>
          <p style={{
            textAlign: 'center',
            fontSize: '1.2rem',
            opacity: 0.7,
            marginBottom: '4rem',
            maxWidth: '600px',
            margin: '0 auto 4rem'
          }}>
            Excellence in every detail, from concept to completion
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {services.map((service, index) => (
              <div
                key={index}
                style={{
                  position: 'relative',
                  height: '400px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.3s',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-10px)';
                  const overlay = e.currentTarget.querySelector('.overlay') as HTMLElement;
                  if (overlay) overlay.style.backgroundColor = 'rgba(0,0,0,0.7)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  const overlay = e.currentTarget.querySelector('.overlay') as HTMLElement;
                  if (overlay) overlay.style.backgroundColor = 'rgba(0,0,0,0.4)';
                }}
              >
                {/* Background Image */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: `url(${service.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                
                {/* Overlay */}
                <div 
                  className="overlay"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    transition: 'background-color 0.3s'
                  }} 
                />
                
                {/* Content */}
                <div style={{
                  position: 'relative',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '2rem',
                  color: '#fff',
                  textAlign: 'center',
                  zIndex: 1
                }}>
                  <div style={{ marginBottom: '1rem' }}>
                    {service.icon}
                  </div>

            <div style={{
              backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
              padding: '3rem',
              borderRadius: '12px',
              boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
              marginTop: '2rem'
            }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>License & Insurance</h3>
              <p style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                <strong>CSLB# 1034839</strong><br />
                <span style={{ opacity: 0.8 }}>
                  Fully licensed and insured for your peace of mind. 
                  We maintain comprehensive liability insurance and are bonded 
                  in accordance with California state requirements.
                </span>
              </p>
            </div>
                  <h3 style={{ fontSize: '1.8rem', marginBottom: '1rem' }}>
                    {service.title}
                  </h3>
                  <p style={{ opacity: 0.9, lineHeight: '1.6' }}>
                    {service.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div style={{
        padding: '5rem 2rem',
        background: isDarkMode 
          ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)' 
          : 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '3rem',
          textAlign: 'center'
        }}>
          {[
            { number: '15+', label: 'Projects Completed' },
            { number: '14', label: 'Years of Excellence' },
            { number: '100%', label: 'Client Satisfaction' },
            { number: 'CSLB# 1034839', label: 'Licensed & Insured' }
          ].map((stat, index) => (
            <div key={index}>
              <h3 style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                {stat.number}
              </h3>
              <p style={{ fontSize: '1.2rem', opacity: 0.7 }}>
                {stat.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const AboutPage: React.FC<PageProps> = ({ isDarkMode, setActiveView, styles }) => {
  return (
    <div style={{ paddingTop: '60px', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        height: '50vh',
        backgroundImage: 'url(https://images.pexels.com/photos/259588/pexels-photo-259588.jpeg?auto=compress&cs=tinysrgb&w=1920&h=800&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)'
        }} />
        <h1 style={{
          fontSize: '4rem',
          color: '#fff',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
        }}>
          About Kent Construction
        </h1>
      </div>

      {/* Content Section */}
      <div style={{ padding: '5rem 2rem', backgroundColor: isDarkMode ? '#0a0a0a' : '#f8f8f8' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          {/* Company Overview */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '4rem',
            marginBottom: '5rem',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ fontSize: '2.5rem', marginBottom: '1.5rem' }}>Building Dreams Since 2010</h2>
              <p style={{ lineHeight: '1.8', opacity: 0.8, marginBottom: '1.5rem' }}>
                Since 2010, Kent Construction has been providing exceptional 
                residential construction services in Southern California. As a boutique construction 
                company, we focus on quality over quantity, ensuring each project receives our 
                full attention and expertise. Our personalized approach has made us the trusted partner 
                for homeowners seeking meticulous craftsmanship and dedicated service.
              </p>
              <p style={{ lineHeight: '1.8', opacity: 0.8 }}>
                Licensed under CSLB# 1034839, we bring over a decade of experience to every project. 
                Our small but skilled team ensures that each home we build or remodel receives 
                the personal attention it deserves. From initial consultation to final walkthrough, 
                we work closely with our clients to bring their vision to life.
              </p>
            </div>
            <div style={{
              height: '400px',
              backgroundImage: 'url(https://images.pexels.com/photos/667838/pexels-photo-667838.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop)',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              borderRadius: '12px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
            }} />
          </div>

          {/* Values Section */}
          <div style={{
            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
            padding: '4rem',
            borderRadius: '12px',
            marginBottom: '5rem'
          }}>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '3rem', textAlign: 'center' }}>Our Core Values</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '3rem'
            }}>
              {[
                { title: 'Quality', desc: 'Uncompromising standards in every aspect of construction' },
                { title: 'Innovation', desc: 'Embracing cutting-edge techniques and sustainable practices' },
                { title: 'Integrity', desc: 'Transparent communication and honest business practices' },
                { title: 'Excellence', desc: 'Exceeding expectations through meticulous attention to detail' }
              ].map((value, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <CheckCircle size={50} style={{ marginBottom: '1rem', color: '#FFD700' }} />
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{value.title}</h3>
                  <p style={{ opacity: 0.7, lineHeight: '1.6' }}>{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Team Section */}
          <div>
            <h2 style={{ fontSize: '2.5rem', marginBottom: '3rem', textAlign: 'center' }}>Leadership Team</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '3rem'
            }}>
              {[
                {
                  name: 'Jeff Lee',
                  role: 'Founder & CEO',
                  image: 'https://images.pexels.com/photos/3785079/pexels-photo-3785079.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop'
                },
                {
                  name: 'Sarah Mitchell',
                  role: 'Chief Design Officer',
                  image: 'https://images.pexels.com/photos/3756679/pexels-photo-3756679.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop'
                },
                {
                  name: 'Michael Chen',
                  role: 'VP of Construction',
                  image: 'https://images.pexels.com/photos/5384445/pexels-photo-5384445.jpeg?auto=compress&cs=tinysrgb&w=400&h=400&fit=crop'
                }
              ].map((member, index) => (
                <div key={index} style={{ textAlign: 'center' }}>
                  <div style={{
                    width: '200px',
                    height: '200px',
                    borderRadius: '50%',
                    backgroundImage: `url(${member.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    margin: '0 auto 1.5rem',
                    boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
                  }} />
                  <h3 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>{member.name}</h3>
                  <p style={{ opacity: 0.7 }}>{member.role}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        padding: '4rem 2rem',
        textAlign: 'center',
        backgroundColor: isDarkMode ? '#000' : '#fff'
      }}>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Ready to Build Your Dream Home?</h2>
        <p style={{ fontSize: '1.2rem', opacity: 0.7, marginBottom: '2rem' }}>
          Let's discuss how we can bring your vision to life
        </p>
        <button
          onClick={() => setActiveView('contact')}
          style={{
            padding: '1.2rem 3rem',
            fontSize: '1.2rem',
            backgroundColor: isDarkMode ? '#FFD700' : '#000',
            color: isDarkMode ? '#000' : '#fff',
            border: 'none',
            cursor: 'pointer',
            transition: 'transform 0.3s',
            fontWeight: 'bold',
            borderRadius: '4px'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          GET IN TOUCH <ArrowRight size={20} style={{ display: 'inline', marginLeft: '0.5rem' }} />
        </button>
      </div>
    </div>
  );
};

export const ProjectsPage: React.FC<PageProps> = ({ isDarkMode, setActiveView, styles }) => {
  const projects = [
    { 
      title: "Modern Hillside Estate", 
      location: "Bel Air, CA", 
      year: "2023",
      description: "12,000 sq ft luxury residence with panoramic city views",
      image: "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=600&h=450&fit=crop",
      category: "Custom Home"
    },
    { 
      title: "Coastal Contemporary", 
      location: "Malibu, CA", 
      year: "2023",
      description: "8,500 sq ft beachfront property with sustainable features",
      image: "https://images.pexels.com/photos/1488267/pexels-photo-1488267.jpeg?auto=compress&cs=tinysrgb&w=600&h=450&fit=crop",
      category: "Custom Home"
    },
    { 
      title: "Urban Penthouse", 
      location: "Beverly Hills, CA", 
      year: "2022",
      description: "6,000 sq ft duplex penthouse with private rooftop garden",
      image: "https://images.pexels.com/photos/2121121/pexels-photo-2121121.jpeg?auto=compress&cs=tinysrgb&w=600&h=450&fit=crop",
      category: "Major Remodel"
    },
    { 
      title: "Mountain Retreat", 
      location: "Aspen, CO", 
      year: "2022",
      description: "10,000 sq ft ski-in/ski-out luxury chalet",
      image: "https://images.pexels.com/photos/731082/pexels-photo-731082.jpeg?auto=compress&cs=tinysrgb&w=600&h=450&fit=crop",
      category: "Custom Home"
    },
    { 
      title: "Desert Oasis", 
      location: "Palm Springs, CA", 
      year: "2021",
      description: "7,500 sq ft mid-century modern renovation",
      image: "https://images.pexels.com/photos/2635038/pexels-photo-2635038.jpeg?auto=compress&cs=tinysrgb&w=600&h=450&fit=crop",
      category: "Major Remodel"
    },
    { 
      title: "Wine Country Estate", 
      location: "Napa Valley, CA", 
      year: "2021",
      description: "15,000 sq ft estate with private vineyard and tasting room",
      image: "https://images.pexels.com/photos/53610/large-home-residential-house-architecture-53610.jpeg?auto=compress&cs=tinysrgb&w=600&h=450&fit=crop",
      category: "Custom Home"
    }
  ];

  const [selectedCategory, setSelectedCategory] = React.useState('All');
  const categories = ['All', 'Custom Home', 'Major Remodel', 'ADU', 'Multi-Unit'];

  const filteredProjects = selectedCategory === 'All' 
    ? projects 
    : projects.filter(p => p.category === selectedCategory);

  return (
    <div style={{ paddingTop: '60px', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        height: '40vh',
        backgroundImage: 'url(https://images.pexels.com/photos/416160/pexels-photo-416160.jpeg?auto=compress&cs=tinysrgb&w=1920&h=600&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)'
        }} />
        <h1 style={{
          fontSize: '4rem',
          color: '#fff',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
        }}>
          Our Portfolio
        </h1>
      </div>

      {/* Filter Section */}
      <div style={{
        padding: '3rem 2rem',
        backgroundColor: isDarkMode ? '#0a0a0a' : '#f8f8f8',
        borderBottom: `1px solid ${isDarkMode ? '#333' : '#ddd'}`
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          gap: '1rem',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}>
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                padding: '0.8rem 2rem',
                backgroundColor: selectedCategory === category 
                  ? (isDarkMode ? '#FFD700' : '#000')
                  : 'transparent',
                color: selectedCategory === category 
                  ? (isDarkMode ? '#000' : '#fff')
                  : (isDarkMode ? '#fff' : '#000'),
                border: `2px solid ${selectedCategory === category 
                  ? (isDarkMode ? '#FFD700' : '#000')
                  : (isDarkMode ? '#333' : '#ddd')}`,
                cursor: 'pointer',
                transition: 'all 0.3s',
                fontWeight: selectedCategory === category ? 'bold' : 'normal',
                borderRadius: '25px'
              }}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      <div style={{
        padding: '5rem 2rem',
        backgroundColor: isDarkMode ? '#000' : '#fff'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '2rem'
        }}>
          {filteredProjects.map((project, index) => (
            <div
              key={index}
              style={{
                position: 'relative',
                borderRadius: '12px',
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'all 0.3s',
                boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
                height: '450px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = '0 15px 40px rgba(0,0,0,0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 5px 20px rgba(0,0,0,0.1)';
              }}
            >
              {/* Project Image */}
              <div style={{
                height: '60%',
                backgroundImage: `url(${project.image})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  backgroundColor: 'rgba(0,0,0,0.7)',
                  color: '#fff',
                  padding: '0.5rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.9rem'
                }}>
                  {project.category}
                </div>
              </div>

              {/* Project Details */}
              <div style={{
                padding: '2rem',
                backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
                height: '40%'
              }}>
                <h3 style={{ fontSize: '1.8rem', marginBottom: '0.5rem' }}>
                  {project.title}
                </h3>
                <p style={{ 
                  fontSize: '1.1rem', 
                  opacity: 0.7, 
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <MapPin size={16} /> {project.location} • {project.year}
                </p>
                <p style={{ opacity: 0.8, lineHeight: '1.6' }}>
                  {project.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div style={{
        padding: '4rem 2rem',
        textAlign: 'center',
        backgroundColor: isDarkMode ? '#0a0a0a' : '#f8f8f8'
      }}>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
          Ready to Create Something Extraordinary?
        </h2>
        <p style={{ fontSize: '1.2rem', opacity: 0.7, marginBottom: '2rem' }}>
          Let's collaborate on your next project
        </p>
        <button
          onClick={() => setActiveView('contact')}
          style={{
            padding: '1.2rem 3rem',
            fontSize: '1.2rem',
            backgroundColor: isDarkMode ? '#FFD700' : '#000',
            color: isDarkMode ? '#000' : '#fff',
            border: 'none',
            cursor: 'pointer',
            transition: 'transform 0.3s',
            fontWeight: 'bold',
            borderRadius: '4px'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          START YOUR PROJECT <ArrowRight size={20} style={{ display: 'inline', marginLeft: '0.5rem' }} />
        </button>
      </div>
    </div>
  );
};

export const ContactPage: React.FC<PageProps> = ({ isDarkMode, styles }) => {
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('Thank you for your inquiry. We will contact you within 24 hours!');
    setFormData({
      name: '',
      email: '',
      phone: '',
      projectType: '',
      budget: '',
      timeline: '',
      message: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div style={{ paddingTop: '60px', minHeight: '100vh' }}>
      {/* Hero Section */}
      <div style={{
        height: '30vh',
        backgroundImage: 'url(https://images.pexels.com/photos/7031406/pexels-photo-7031406.jpeg?auto=compress&cs=tinysrgb&w=1920&h=400&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)'
        }} />
        <h1 style={{
          fontSize: '4rem',
          color: '#fff',
          position: 'relative',
          zIndex: 1,
          textAlign: 'center',
          textShadow: '2px 2px 4px rgba(0,0,0,0.8)'
        }}>
          Let's Build Together
        </h1>
      </div>

      {/* Contact Section */}
      <div style={{
        padding: '5rem 2rem',
        backgroundColor: isDarkMode ? '#0a0a0a' : '#f8f8f8'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
          gap: '4rem'
        }}>
          {/* Contact Form */}
          <div style={{
            backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
            padding: '3rem',
            borderRadius: '12px',
            boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
          }}>
            <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Project Inquiry Form</h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      fontSize: '1rem',
                      backgroundColor: isDarkMode ? '#0a0a0a' : '#f5f5f5',
                      border: `1px solid ${isDarkMode ? '#333' : '#ddd'}`,
                      borderRadius: '6px',
                      color: isDarkMode ? '#fff' : '#000',
                      transition: 'border-color 0.3s'
                    }}
                  />
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      fontSize: '1rem',
                      backgroundColor: isDarkMode ? '#0a0a0a' : '#f5f5f5',
                      border: `1px solid ${isDarkMode ? '#333' : '#ddd'}`,
                      borderRadius: '6px',
                      color: isDarkMode ? '#fff' : '#000',
                      transition: 'border-color 0.3s'
                    }}
                  />
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#0a0a0a' : '#f5f5f5',
                    border: `1px solid ${isDarkMode ? '#333' : '#ddd'}`,
                    borderRadius: '6px',
                    color: isDarkMode ? '#fff' : '#000',
                    transition: 'border-color 0.3s'
                  }}
                />
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
                    Project Type
                  </label>
                  <select
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      fontSize: '1rem',
                      backgroundColor: isDarkMode ? '#0a0a0a' : '#f5f5f5',
                      border: `1px solid ${isDarkMode ? '#333' : '#ddd'}`,
                      borderRadius: '6px',
                      color: isDarkMode ? '#fff' : '#000',
                      transition: 'border-color 0.3s'
                    }}
                  >
                    <option value="">Select type</option>
                    <option value="custom-home">Custom Home</option>
                    <option value="adu">ADU</option>
                    <option value="multi-unit">Multi-Unit</option>
                    <option value="remodel">Major Remodel</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
                    Budget Range
                  </label>
                  <select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '0.8rem',
                      fontSize: '1rem',
                      backgroundColor: isDarkMode ? '#0a0a0a' : '#f5f5f5',
                      border: `1px solid ${isDarkMode ? '#333' : '#ddd'}`,
                      borderRadius: '6px',
                      color: isDarkMode ? '#fff' : '#000',
                      transition: 'border-color 0.3s'
                    }}
                  >
                    <option value="">Select budget</option>
                    <option value="under-1m">Under $1M</option>
                    <option value="1m-2m">$1M - $2M</option>
                    <option value="2m-5m">$2M - $5M</option>
                    <option value="5m-10m">$5M - $10M</option>
                    <option value="over-10m">Over $10M</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '1rem', fontWeight: '500' }}>
                  Project Details *
                </label>
                <textarea
                  name="message"
                  required
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your vision..."
                  style={{
                    width: '100%',
                    padding: '0.8rem',
                    fontSize: '1rem',
                    backgroundColor: isDarkMode ? '#0a0a0a' : '#f5f5f5',
                    border: `1px solid ${isDarkMode ? '#333' : '#ddd'}`,
                    borderRadius: '6px',
                    color: isDarkMode ? '#fff' : '#000',
                    resize: 'vertical',
                    transition: 'border-color 0.3s'
                  }}
                />
              </div>
              
              <button
                type="submit"
                style={{
                  padding: '1rem 2rem',
                  fontSize: '1.1rem',
                  backgroundColor: isDarkMode ? '#FFD700' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  fontWeight: 'bold',
                  borderRadius: '6px',
                  marginTop: '1rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                SUBMIT INQUIRY
              </button>
            </form>
          </div>

          {/* Contact Information */}
          <div>
            <div style={{
              backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
              padding: '3rem',
              borderRadius: '12px',
              boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
              marginBottom: '2rem'
            }}>
              <h2 style={{ fontSize: '2rem', marginBottom: '2rem' }}>Get in Touch</h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Phone size={24} style={{ color: '#FFD700' }} />
                  <div>
                    <p style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Phone</p>
                    <p style={{ opacity: 0.8 }}>(555) 123-4567</p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <Mail size={24} style={{ color: '#FFD700' }} />
                  <div>
                    <p style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Email</p>
                    <p style={{ opacity: 0.8 }}>info@kentconstruction.com</p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <MapPin size={24} style={{ color: '#FFD700' }} />
                  <div>
                    <p style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Office</p>
                    <p style={{ opacity: 0.8 }}>
                      713 W. Duarte Rd. G860<br />
                      Arcadia, CA 91007
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: isDarkMode ? '#1a1a1a' : '#fff',
              padding: '3rem',
              borderRadius: '12px',
              boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1.5rem' }}>Office Hours</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Monday - Friday</span>
                  <span style={{ opacity: 0.8 }}>8:00 AM - 6:00 PM</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Saturday</span>
                  <span style={{ opacity: 0.8 }}>9:00 AM - 4:00 PM</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Sunday</span>
                  <span style={{ opacity: 0.8 }}>By Appointment</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Map Section */}
      <div style={{
        height: '400px',
        backgroundImage: 'url(https://images.pexels.com/photos/681335/pexels-photo-681335.jpeg?auto=compress&cs=tinysrgb&w=1920&h=400&fit=crop)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.95)',
            padding: '2rem 3rem',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#000' }}>Visit Our Showroom</h3>
            <p style={{ color: '#666' }}>Experience our craftsmanship firsthand</p>
          </div>
        </div>
      </div>
    </div>
  );
};